import type { QueryKey, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";

import { useServices } from "@src/context/ServicesProvider";
import { useScopedFetchProviderUrl } from "@src/hooks/useScopedFetchProviderUrl";
import { SKIP_REPORTING_PROVIDER_POLL_FAILURE } from "@src/services/query-error-policy/query-error-policy";
import type { DashboardData } from "@src/types/dashboard";
import type {
  ApiProviderDetail,
  ApiProviderList,
  ApiProviderLocation,
  ApiProviderRegion,
  ProviderStatus,
  ProviderStatusDto,
  ProviderVersion
} from "@src/types/provider";
import type { ProviderAttributesSchema } from "@src/types/providerAttributes";
import { ApiUrlService } from "@src/utils/apiUtils";
import { providerStatusToDto } from "@src/utils/providerUtils";
import { findFirstReachableProvider } from "./findFirstReachableProvider";
import { QueryKeys } from "./queryKeys";

export function useProviderDetail(
  owner: string,
  options: Omit<UseQueryOptions<ApiProviderDetail | null>, "queryKey" | "queryFn"> = {}
): UseQueryResult<ApiProviderDetail | null> {
  const { publicConsoleApiHttpClient } = useServices();
  return useQuery({
    queryKey: QueryKeys.getProviderDetailKey(owner) as QueryKey,
    queryFn: async () => {
      if (!owner) return null;
      const response = await publicConsoleApiHttpClient.get(ApiUrlService.providerDetail(owner));
      return response.data;
    },
    ...options
  });
}

/** Provider status polls own their error-reporting policy, so `meta` is not caller-settable and cannot silently drop it. */
type ProviderStatusQueryOptions = Omit<UseQueryOptions<ProviderStatusDto>, "queryKey" | "queryFn" | "meta">;

export function useProviderStatus(provider: ApiProviderList | undefined | null, options: ProviderStatusQueryOptions = {}): UseQueryResult<ProviderStatusDto> {
  const fetchProviderUrl = useScopedFetchProviderUrl(provider);
  return useQuery({
    queryKey: QueryKeys.getProviderStatusKey(provider?.hostUri || ""),
    queryFn: async () => {
      const [statusResponse, versionResponse] = await Promise.all([fetchProviderUrl<ProviderStatus>("/status"), fetchProviderUrl<ProviderVersion>("/version")]);
      return providerStatusToDto(statusResponse.data, versionResponse.data || {});
    },
    ...options,
    meta: SKIP_REPORTING_PROVIDER_POLL_FAILURE
  });
}

/** Keeps one unresponsive provider from holding up the rest of its batch. */
const PROVIDER_STATUS_PROBE_TIMEOUT_MS = 5000;

/** Keyed on the placement rather than the candidates, so a bid poll no longer abandons an in-flight probe and restarts it. */
export function useFirstReachableProvider(
  placementKey: string | null,
  providers: ApiProviderList[] | undefined | null,
  options: Omit<UseQueryOptions<ApiProviderList | null>, "queryKey" | "queryFn"> = {}
): UseQueryResult<ApiProviderList | null> {
  const { providerProxy } = useServices();
  const providerList = providers ?? [];
  return useQuery({
    queryKey: QueryKeys.getFirstReachableProviderKey(placementKey ?? ""),
    queryFn: () =>
      findFirstReachableProvider(providerList, provider =>
        providerProxy.request<ProviderStatus>("/status", {
          providerIdentity: { owner: provider.owner, hostUri: provider.hostUri },
          timeout: PROVIDER_STATUS_PROBE_TIMEOUT_MS
        })
      ),
    ...options
  });
}

export interface ProviderActiveLeasesGraph {
  currentValue: number;
  compareValue: number;
  snapshots: { date: string; value: number }[];
}

export function useProviderActiveLeasesGraph(providerAddress: string, options = {}) {
  const { publicConsoleApiHttpClient } = useServices();
  return useQuery({
    queryKey: QueryKeys.getProviderActiveLeasesGraph(providerAddress),
    queryFn: () =>
      publicConsoleApiHttpClient.get<ProviderActiveLeasesGraph>(ApiUrlService.providerActiveLeasesGraph(providerAddress)).then(response => response.data),
    ...options
  });
}

export function useProviderAttributesSchema(options = {}) {
  const { publicConsoleApiHttpClient } = useServices();
  return useQuery({
    queryKey: QueryKeys.getProviderAttributesSchema(),
    queryFn: () => publicConsoleApiHttpClient.get<ProviderAttributesSchema>(ApiUrlService.providerAttributesSchema()).then(response => response.data),
    ...options,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
}

export type ProviderSearchSort = "active-leases-desc" | "active-leases-asc" | "wallet-leases-desc" | "wallet-active-leases-desc" | "gpus-desc";

export interface ProviderSearchParams {
  search?: string;
  online?: boolean;
  audited?: boolean;
  addresses?: string[];
  regions?: string[];
  gpu?: boolean;
  gpuModels?: string[];
  sort: ProviderSearchSort;
  walletAddress?: string;
  skip: number;
  limit: number;
}

export interface ProviderSearchPage {
  providers: ApiProviderList[];
  pagination: { total: number; skip: number; limit: number; hasMore: boolean };
}

export function useProviderSearch(params: ProviderSearchParams, options: { enabled?: boolean } = {}) {
  const { publicConsoleApiHttpClient } = useServices();
  return useQuery({
    queryKey: QueryKeys.getProviderSearchKey(params),
    queryFn: () =>
      publicConsoleApiHttpClient
        .get<{ data: ProviderSearchPage }>(ApiUrlService.providerSearch(), {
          params: { ...params, addresses: params.addresses?.join(","), regions: params.regions?.join(","), gpuModels: params.gpuModels?.join(",") }
        })
        .then(response => response.data.data),
    placeholderData: keepPreviousData,
    enabled: options.enabled
  });
}

export function useDashboardData() {
  const { publicConsoleApiHttpClient } = useServices();
  return useQuery({
    queryKey: QueryKeys.getDashboardDataKey(),
    queryFn: () => publicConsoleApiHttpClient.get<DashboardData>(ApiUrlService.dashboardData()).then(response => response.data)
  });
}

export function useProviderLocations() {
  const { publicConsoleApiHttpClient } = useServices();
  return useQuery({
    queryKey: QueryKeys.getProviderLocationsKey(),
    queryFn: () => publicConsoleApiHttpClient.get<{ data: ApiProviderLocation[] }>(ApiUrlService.providerLocations()).then(response => response.data.data)
  });
}

/** Each address is its own query, so a provider read for one view is reused by the next and a growing address list only fetches the newcomers. */
export function useProvidersByAddresses(addresses: readonly string[], options: { enabled?: boolean } = {}) {
  const { providerLookup } = useServices();
  return useQueries({
    queries: [...new Set(addresses)].map(address => ({
      queryKey: QueryKeys.getProviderByAddressKey(address),
      queryFn: () => providerLookup.findByAddress(address),
      enabled: options.enabled
    })),
    combine: combineProviderLookups
  });
}

function combineProviderLookups(results: UseQueryResult<ApiProviderList | null>[]) {
  return {
    data: results.flatMap(result => (result.data ? [result.data] : [])),
    isLoading: results.some(result => result.isLoading),
    isFetching: results.some(result => result.isFetching)
  };
}

export function useProviderRegions(options = {}) {
  const { publicConsoleApiHttpClient } = useServices();
  return useQuery({
    queryKey: QueryKeys.getProviderRegionsKey(),
    queryFn: () => publicConsoleApiHttpClient.get<ApiProviderRegion[]>(ApiUrlService.providerRegions()).then(response => response.data),
    ...options
  });
}
